import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import React, { useMemo, useRef, useState, useContext } from 'react';
import { buttonReset, IconButton } from "../Button";
import styled, { css } from 'styled-components';
import { IconX } from "@frameio/vapor-icons/build/esm/icons/IconX";
import { theme, whenFocused, whenActive, whenHovered } from "../../theme";
import { easeCurves, eases } from "../../motion/eases";
import { textStyles, truncateStyles } from "../Text";
import { Box } from "../Box";
import { motion, PresenceContext } from 'framer-motion';
import { useDescendant, useForkedRef } from "../../hooks";
var DELETE_LABEL = 'Delete';
export var Chip = React.forwardRef(function Chip(_ref, forwardedRef) {
  var avatar = _ref.avatar,
      _ref$clickable = _ref.clickable,
      clickable = _ref$clickable === void 0 ? false : _ref$clickable,
      iconBeforeProp = _ref.iconBefore,
      onDeleteProp = _ref.onDelete,
      onClickProp = _ref.onClick,
      children = _ref.children,
      onKeyDownProp = _ref.onKeyDown,
      _ref$color = _ref.color,
      color = _ref$color === void 0 ? 'default' : _ref$color,
      disabled = _ref.disabled,
      _ref$id = _ref.id,
      id = _ref$id === void 0 ? null : _ref$id,
      other = _objectWithoutProperties(_ref, ["avatar", "clickable", "iconBefore", "onDelete", "onClick", "children", "onKeyDown", "color", "disabled", "id"]);

  var iconAfterRef = useRef(null);
  var chipRef = useRef(null);
  var ref = useForkedRef(forwardedRef, chipRef);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      focused = _useState2[0],
      setFocused = _useState2[1];

  var presence = useContext(PresenceContext);
  var isPresent = presence ? presence.isPresent : undefined;
  useDescendant(id, chipRef, {
    isPresent: isPresent
  });
  /**
   * Render either an avatar or icon
   */

  var iconBefore = useMemo(function () {
    if (avatar && React.isValidElement(avatar)) {
      return /*#__PURE__*/React.createElement(AvatarPadding, null, React.cloneElement(avatar, {
        size: 20,
        'aria-hidden': true
      }));
    }

    if (iconBeforeProp && React.isValidElement(iconBeforeProp)) {
      return /*#__PURE__*/React.createElement(IconPadding, null, React.cloneElement(iconBeforeProp, {
        size: 16,
        'aria-hidden': true
      }));
    }

    return null;
  }, [avatar, iconBeforeProp]);
  /**
   * Render a delete icon
   */

  var iconAfter = useMemo(function () {
    function onDelete(e) {
      e.stopPropagation();
      onDeleteProp();
    }

    if (onDeleteProp && !clickable) {
      return /*#__PURE__*/React.createElement(IconButton, {
        "data-testid": "chip-iconafter-button",
        disabled: disabled,
        size: "small",
        label: DELETE_LABEL,
        variant: "link",
        onClick: onDelete,
        icon: /*#__PURE__*/React.createElement(IconX, null)
      });
    }

    if (onDeleteProp && clickable) {
      return /*#__PURE__*/React.createElement(Box, {
        "data-testid": "chip-iconafter-box",
        ref: iconAfterRef,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexShrink: "0",
        width: 24,
        onMouseDown: function onMouseDown(e) {
          return e.preventDefault();
        }
      }, /*#__PURE__*/React.createElement(IconX, {
        size: 16
      }));
    }

    return null;
  }, [clickable, disabled, onDeleteProp]);
  /**
   * Handle clicks
   */

  function onClick(e) {
    // If the user clicks the "x" icon and the entire
    // chip is clickable, stop propagation.
    if (iconAfterRef.current && (e.target === iconAfterRef.current || iconAfterRef.current.contains(e.target))) {
      e.stopPropagation();

      if (onDeleteProp) {
        onDeleteProp();
      }

      return;
    }

    chipRef.current.focus();
    e.stopPropagation();

    if (onClickProp) {
      onClickProp(e);
    }
  }

  var isSelected = focused && clickable;
  return /*#__PURE__*/React.createElement(StyledChip, _extends({
    "data-testid": "styled-chip",
    as: clickable ? motion.button : motion.div,
    transition: {
      delay: 0,
      duration: 0.2,
      ease: eases.primary
    },
    initial: {
      opacity: 0,
      scale: 0.85
    },
    animate: {
      opacity: disabled ? 0.3 : 1,
      scale: 1
    },
    exit: {
      opacity: 0,
      scale: 0.85
    },
    $color: color,
    $isSelected: isSelected,
    disabled: disabled,
    onClick: onClick,
    onKeyDown: onKeyDownProp,
    onFocus: function onFocus() {
      return setFocused(true);
    },
    onBlur: function onBlur() {
      return setFocused(false);
    },
    ref: ref
  }, other), iconBefore, /*#__PURE__*/React.createElement(StyledText, {
    $hasAvatar: !!avatar,
    $hasIconBefore: !!iconBefore,
    $hasIconAfter: !!onDeleteProp
  }, children), iconAfter);
});
var AVATAR_PADDING = '2px';
export var StyledText = styled.span.withConfig({
  displayName: "Chip__StyledText",
  componentId: "vapor__k0z2hg-0"
})(["", ";", ";line-height:", ";margin:0 ", ";margin-left:", ";", ""], truncateStyles, textStyles.secondary, theme.spaces[24], theme.spaces[8], function (p) {
  if (p.$hasAvatar) {
    return theme.spaces[4];
  }

  if (p.$hasIconBefore) {
    return theme.spaces[2];
  }

  return null;
}, function (p) {
  return p.$hasIconAfter && "\n    margin-right: 0;\n  ";
});
var AvatarPadding = styled.div.withConfig({
  displayName: "Chip__AvatarPadding",
  componentId: "vapor__k0z2hg-1"
})(["padding:", " 0 ", " ", ";"], AVATAR_PADDING, AVATAR_PADDING, AVATAR_PADDING);
var IconPadding = styled.div.withConfig({
  displayName: "Chip__IconPadding",
  componentId: "vapor__k0z2hg-2"
})(["padding-left:", ";display:flex;"], theme.spaces[4]);
export var StyledChip = styled(motion.div).withConfig({
  displayName: "Chip__StyledChip",
  componentId: "vapor__k0z2hg-3"
})(["", ";height:", ";justify-content:space-between;transition:background-color 0.15s ", ",color 0.15s ", ";border-radius:100px;user-select:none;overflow:hidden;display:inline-flex;vertical-align:top;align-items:center;will-change:transform;outline:0;padding:0;cursor:default;max-width:100%;", ";", ";", "{", ";}", "{", ";}", ";", ""], textStyles.secondary, theme.spaces[24], easeCurves.primary, easeCurves.primary, function (p) {
  return p.as === motion.button && buttonReset;
}, baseStyles, whenHovered, hoverStyles, whenFocused, selectedStyles, function (p) {
  return p.$isSelected && selectedStyles;
}, function (p) {
  return p.disabled && "\n      pointer-events: none;\n      opacity: 0.3;\n      background-color: rgba(0, 0, 0, 0.5);\n    ";
});

function baseStyles(p) {
  if (p.$color === 'default') {
    return css(["background-color:", ";color:", ";"], theme.colors.palette.grey20, theme.colors.palette.grey80);
  }

  return css(["background-color:", ";color:", ";"], theme.colors.palette["".concat(p.$color, "900")], theme.colors.palette["".concat(p.$color, "500")]);
}

function hoverStyles(p) {
  if (p.$color === 'default') {
    return css(["color:", ";background-color:", ";"], theme.colors.palette.tintA90, theme.colors.palette.tintA30);
  }

  return css(["background-color:", ";"], theme.colors.palette["".concat(p.$color, "800")]);
}

function selectedStyles(p) {
  if (p.$color === 'default') {
    return css(["background-color:", ";color:", ";", ",", "{background-color:", ";color:", ";}"], theme.colors.palette.brandPrimary, theme.colors.palette.grey100, whenHovered, whenActive, theme.colors.palette.brandPrimary, theme.colors.palette.grey100);
  }

  return css(["background-color:", ";color:", ";", ",", "{background-color:", ";color:", ";}"], theme.colors.palette["".concat(p.$color, "500")], theme.colors.palette.grey10, whenHovered, whenActive, theme.colors.palette["".concat(p.$color, "500")], theme.colors.palette.grey10);
}